/*
学習パスの項目から、ゆめさくクラウドエディターの手順 (guide) を開くための決まり。

# 項目と章の対応
本番の学習パスのデータ (team_curriculums) は変えず、この表 (項目の id から章の ID) で持つ。
項目はいつもどおりブラウザの中で学び、カードの下の「クラウドエディターで開く」から同じ題材を本物の環境で手を動かす。
手で書いた章は、項目の章の確かめにすべて通ると、api-go が学習パスの項目を済みにする (2026-10-15 の代表の決定。pathItemCompletions)。
講師への提出の手順は 2026-10-20 の代表の決定で外した。宿題も確かめが全部通れば済み。
道場は数えない (2026-10-13 の決定)。

# 誰に出すか
"sandbox" (既定) は、管理者と講師と、api-go が学習パスの章を見せると答えた人 (layout の loader の pathEditorReady。eligibility の pathReady) に出す。
受講生に章を見せる段は api-go の環境変数 SANDBOX_PATH_STUDENT_ACCESS で、エディターに入れる段 (SANDBOX_STUDENT_ACCESS) とは別に開け閉めする
(2026-10-23、サイドバーからは受講生もエディターに入れ、章は全問の確かめのあとに開く)。どちらも client の配備は要らない。章の段が off のあいだは受講生に章は出ない。
入れない人には「準備中」と出し、開かせない。api-go の PathCloudEditorAudience も同じ値 (path_audience_test.go が見る)。

# 戻り先
クラウドエディターには ?from=<鍵> で来る。鍵は下の表にあるものだけを受け、URL をそのまま戻り先にしない
(よその場所へ飛ばすリンクを作らせない)。
*/
import { findGuideEntry, type GuideEntry } from "@/pages/luagate/cloudeditor/guide-index";
import { completionAllowed, itemGuideCompletion, lessonItems } from "@/pages/luagate/cloudeditor/item-completion";
import { kindOf } from "@/pages/luagate/cloudeditor/kinds";
import { LESSON_GUIDE_INDEX, LESSON_GUIDE_PATH_ITEMS, LESSON_GUIDE_PATH_OUTSIDE, LESSON_GUIDE_PATH_ROLES } from "@/pages/luagate/cloudeditor/lesson-guides.generated";
import { learningAssetRelationLabel } from "@/components/luagate/learning-assets/presentation";
import { PATH_LEARNING_ASSET_RELATIONS, type PathLearningAssetRelation } from "@/components/luagate/learning-assets/types";
import type { KindCapacity } from "@/pages/luagate/cloudeditor/ide";
import type { Guide } from "@/pages/luagate/cloudeditor/guides";
import { hasCheck, isQuizGuide } from "@/pages/luagate/cloudeditor/progress";

/** 学習パスに「クラウドエディターで開く」を出す相手。sandbox は管理者と講師と、入場券の段で入れる人 */
export const PATH_CLOUDEDITOR_AUDIENCE: PathCloudEditorAudience = "sandbox";

export type PathCloudEditorAudience = "staff" | "sandbox" | "everyone";

/**
 * 手で書いた章を開く項目 (team_curriculums.id)。LuaGate の既定の学習パスの項目 (2026-10-11 に本番を読み取りで確かめた id)。
 * 写した章 (LESSON_GUIDE_PATH_ITEMS) と同じ項目があれば、こちらが先
 */
export const WRITTEN_PATH_GUIDES: Readonly<Record<string, string>> = {
  // ポータルサイトへようこそ (表示順 1、記事)。学習パスをエディターで歩くとき、最初に開く章を、開いた瞬間に動くコードと実行のある章にする。
  // 次の項目 (表示順 2) は Mattermost の章で、外での作業なのでエディターの真ん中が空く。最初に空の画面を見せない (2026-10-15 代表の決定)。
  // 学習パスの並び (team_curriculums の表示順) と番号の URL は変えない。first-python は最初の一歩のカードの章でもある
  "e309cbd8-787d-424d-b35e-093d8dac9595": "first-python",
  // 宿題 HTMLでカードUIを作ってみよう (表示順 146)。HTML と CSS を本物のエディターで書き、右の画面で見る
  "a8837068-2e05-4730-ae5e-f5171519d344": "first-web",
  // React基礎コース (表示順 160)。React と Express の 2 つのファイルをまたいで TODO アプリを書き換える
  "f753a64d-8c90-40cd-85eb-3b8768d03d90": "todo-app",
  // 宿題 Markdown記法を覚えよう (表示順 26)。README.md を書き、確かめが全部通れば済み
  "06deaa49-3cf3-479b-a15c-f76be9508f0f": "hw-markdown",
  // 宿題 PC環境構築とショートカットを覚えよう (表示順 24)。エディターと端末のショートカットを稽古し、記録を書く
  "119872b6-7a75-474f-bcb4-c3a7ccb0150d": "hw-shortcuts",
  // 宿題 Google検索のコツを身につけよう (表示順 25)。検索ワードを決まりで書き、自分のブラウザで検索した記録を書く
  "0ad97c5e-c857-4166-a8d2-f7da4d7899ff": "hw-search",
  // 宿題 今月学んだことをnoteにアウトプット (表示順 46)。記事の下書きを Markdown で書き、見直してから note で公開する
  "f870fd6c-1d14-4b38-a8b4-7351c96a5468": "hw-note",
  // Mattermostに入ろう (表示順 2)。外での作業の自己申告 (attest) の最初の 1 本
  "9a56c43c-0db3-440c-b7e9-150aea9fa173": "mattermost-join",
  // Linux道場 商店街サーバー障害対応の10問 (表示順 55)。道場の 1 章目から、本物の端末で解く
  "044f74d0-f940-4053-a778-91a416bca58c": "dojo-lx-01",
  // 宿題 Webの流れを図にして見てほしい点を添えよう (表示順 43)。つながりと順を mermaid の字で書く
  "f870fd6c-1d14-4b38-a8b4-7351c96a546a": "hw-web-flow",
  // 宿題 ログインの仕組みを図に書き起こしてみよう (表示順 45)。成功と失敗の道、次のページ、誤りの直しを書く
  "3b5a1176-ba89-4052-86b2-0e40b69922c8": "hw-login-flow",
  // SQL入門コースを実際にやってみよう (表示順 81)。項目はコース sql-basics を指すだけなので、コースのレッスンから写した章の先頭から開く
  "6ceb49fa-c7c3-4bed-bbf4-334013b9a76a": "sql-basics-what-is-database",
  // オブジェクト指向 - 5 飼育レポート (表示順 124)。整形を差し替えられる形に分け、HTML を足すだけで増やす
  "f0b76e1b-2310-4f19-9c56-0d3b3dbf8d58": "hw-oop-report",
  // お手本コードを読もう 実践プロジェクト0 TODOアプリ (表示順 134)。読んだ順を書き、誤りを直し、列と絞り込みを足す
  "4f8f3f31-4e6e-576e-8b37-4ec49f676b3f": "hw-todo-read",
  // 設計判断 - 3 認証方式はどう選ぶ？ (表示順 233)。セッションと JWT を作り、取り消しの差を手で見る
  "c4935e23-3ac8-49c5-92b3-917f308ffd04": "hw-auth-choice",
  // コードレビュー - 1 バグの匂いを見抜く (表示順 238)。試験で誤りを示し、仕様の通りに直す
  "47c2b852-0200-4533-a78b-30c08ff5d018": "hw-review-bug",
  // 宿題 クラス図を作成しよう (表示順 264)。mermaid の classDiagram で昇段審査を描く
  "559df1e2-4d83-4838-9533-e31001802362": "hw-class-diagram",
  // 宿題 シーケンス図を作成しよう (表示順 265)。mermaid の sequenceDiagram でログインと申し込みを描く
  "d9cdf58b-ba57-42aa-90c6-1f31342486da": "hw-sequence-diagram",
  // 宿題 フォルダ整理の基本を学ぼう (表示順 27)。自分の PC に Developer フォルダを作り、日報に 3 つを投稿する外での作業の自己申告 (attest)
  "8e4bb835-3d0a-41db-a352-81aca189b39a": "hw-folder-tidy",
  // 宿題 自分のPCスペックを調べてみよう (表示順 28)。自分の PC の OS、CPU、メモリ、ストレージを調べ、日報に投稿する外での作業の自己申告 (attest)
  "ed6ed679-204e-46a2-aa7b-b8029929cf45": "hw-pc-spec",
  // 宿題 ファイルパーミッションとユーザー管理 (表示順 53)。ls -l を読み、chmod と umask で札を掛ける
  "d9fed3e1-214b-488b-9a16-c1142571f869": "hw-permissions",
  // データベース入門 (表示順 64)。CSV の帳面の傷を数え、決まりのある表に移して psql で聞く
  "5fa64a10-b5bc-4f8e-ab39-578083390922": "db-why-database",
  // データベース環境構築 (表示順 79)。PostgreSQL を起こしてつなぎ、ロールを分け、写しを取って戻す
  "897d8496-40f0-478f-a9f0-7e28d8814cfc": "db-setup-postgres",
  // 宿題 Webサーバーを構築しよう (表示順 268)。python3 -m http.server で掲示板を立て、curl で見て、畳む
  "e1fda75e-0224-42bb-8d5b-7cc50a50ff55": "hw-web-server",
  // Pythonオブジェクト指向コース (表示順 172)。道場の武具庫で、抽象クラス、プロパティ、Protocol、増やしても変えない形を書く
  "3e0fa711-4fa1-4d83-8237-051cfcb3399d": "oop-armory",
  // NumPyの基礎（表示順 174）。稽古の記録の表を、for を書かずに配列で計算する
  "9925c687-32f0-4932-b429-1e8a38036e46": "numpy-training",
  // Pandasの基礎（表示順 175）。茶屋の売上帳を DataFrame で読み、選び、まとめ、書き出す
  "94a43daa-13d3-4d1b-bf7f-9387e475a190": "pandas-teahouse",
  // データの可視化（表示順 176）。茶屋の売上と心付けを折れ線、棒、ヒストグラム、散布図で描く
  "ea055368-23c1-492d-bda6-06991a1a2e6d": "viz-teahouse",
  // 機械学習の基礎概念（表示順 177）。分け方、誤差、過学習、教師なしを numpy で書く
  "3a4fc7f1-71be-4ee8-a9e3-1d55060aea38": "ml-teahouse",
  // いちばん近い点で色を当てる分類器を作ろう (学習パス v2 の data-ai 70)。AI 道場の第六の巻 (最近傍法を素の Python で) を解く。
  // 道場の章なので章の合格では済まず、項目の頁のミニテストと「済みにする」で済む。scikit-learn の入った章は無い (2026-10-22 代表「つなぎ先は任せた」)
  "e69be99c-47bd-4ba6-b67b-3a8d7c659620": "dojo-ai",
  // ニューラルネットワークの仕組み（表示順 179）。門番の神経を numpy で組み、XOR を学ばせる
  "18843d80-881d-4fa3-9733-ba13b4fef428": "nn-gatekeeper",
  // SQL道場 図書館DBに挑む10問 (表示順 86)
  "cbff6e8d-7181-4b6f-971b-e5db0afb919b": "dojo-sql-01",
  // Python道場 商店街ポイントカードの10問 (表示順 97)
  "9b14e015-d64a-400c-aa4e-d20231fd24db": "dojo-py-01",
  // アルゴリズム道場 カメ師範の十の巻 (表示順 100)
  "6d5865f7-4c57-41f8-b285-c1738edbc507": "dojo-al-01",
  // Java道場 ハレノヒ珈琲のレジを作る10問 (表示順 142)
  "e5f96c1e-ef5e-4c80-8006-015708eaff10": "dojo-java-01",
  // 実践PJ1 ECサイト構築 (表示順 145)。教材の 7-2 を、Tomcat と PostgreSQL の入った本物の環境で進める
  "7e06370e-9cdd-50d5-8156-c7df5d4aff87": "ecsite-ch7-2",
};

/** 道場の章 (例 dojo-sql-01 から dojo-sql-10) の並び */
const dojoChapters = (prefix: string): readonly string[] => Array.from({ length: 10 }, (_, i) => `${prefix}-${String(i + 1).padStart(2, "0")}`);

/** 道場の章か。道場は学習パスの進みに数えない (2026-10-13 の決定) */
export function isDojoGuide(guideId: string): boolean {
  return guideId.startsWith("dojo-");
}

/** 手で書いた章の項目 (team_curriculums.id) と、その章の並び。項目の入口の章だけのときは 1 本 */
function writtenItemChapters(): [string, readonly string[]][] {
  return Object.entries(WRITTEN_PATH_GUIDES).map(([item, first]) => [item, WRITTEN_PATH_LESSONS[item] ?? [first]]);
}

/** 項目の完了の証拠になる手順 (gateway が確かめて api-go に合格を書く手順)。「できた」と自己申告は入れない */
export function evidenceSteps(guide: Pick<Guide, "steps">): string[] {
  return guide.steps.filter((step) => hasCheck(step)).map((step) => step.id);
}

/**
 * 手で書いた章の項目のうち、章の合格で api-go が項目を済みにするものか (章の中身を読まずに、項目の形だけで決める)。
 * 道場、問いの章 (合格で項目が完了になる別の道)、写したレッスンの章 (レッスンの完了に数える)、エディターの外のレッスンを含む項目は入れない
 */
function itemCompletesByChapters(item: string, chapters: readonly string[], isQuiz: (id: string) => boolean): boolean {
  return !chapters.some((id) => isDojoGuide(id) || id in LESSON_GUIDE_INDEX || isQuiz(id)) && !(item in WRITTEN_PATH_OUTSIDE);
}

/**
 * 章の合格で学習パスの項目を済みにする表 (api-go の path_items_gen.go に書き出す。bun run cloudeditor:guides)。
 * 項目の章をすべて開き、どの章も証拠になる手順を 1 つ以上持つ項目だけを載せる。手順は章ごとの証拠の手順 (evidenceSteps)。
 * 自己申告だけの章 (Mattermost に入ろう、宿題 27 と 28) は、サーバが確かめた合格が無いので載せない
 */
export function pathItemCompletions(guides: Readonly<Record<string, Pick<Guide, "id" | "steps">>>): { itemId: string; chapters: { guide: string; steps: string[] }[] }[] {
  return writtenItemChapters()
    .filter(([item, chapters]) => itemCompletesByChapters(item, chapters, (id) => !!guides[id] && isQuizGuide(guides[id])))
    .flatMap(([item, chapters]) => {
      const rows = chapters.map((id) => {
        const guide = guides[id];
        if (!guide) throw new Error(`項目 ${item} の章 ${id} がありません`);
        return { guide: id, steps: evidenceSteps(guide) };
      });
      // 一部の章だけが証拠を持つ項目は、決まりが決まっていない。黙って外さず、書き出しで止める
      const empty = rows.filter((row) => row.steps.length === 0).map((row) => row.guide);
      if (empty.length === rows.length) return [];
      if (empty.length > 0) throw new Error(`項目 ${item} の章 ${empty.join(", ")} に、サーバが確かめる手順がありません`);
      return [{ itemId: item, chapters: rows }];
    })
    .sort((a, b) => a.itemId.localeCompare(b.itemId));
}

/**
 * 章の合格で、学習パスの項目が済みになる章か (pathItemCompletions に載る章)。板はこの章で「学習パスのこの項目を済みにしました」を出す。
 * 項目の形は表と同じ決まりで見て、証拠の手順は開いている章の分だけ見る (項目の残りの章は、書き出しが同じ決まりで確かめている)
 */
export function completesPathItem(guide: Pick<Guide, "id" | "steps">): boolean {
  const found = writtenItemChapters().find(([, chapters]) => chapters.includes(guide.id));
  if (!found) return false;
  const [item, chapters] = found;
  return itemCompletesByChapters(item, chapters, (id) => id === guide.id && isQuizGuide(guide)) && evidenceSteps(guide).length > 0;
}

/**
 * 学習パスの進み (項目の完了) に数える章か。写した章は数える (元のレッスンの完了か、項目の read-done)。
 * 手で並べた項目の章は、問いの章 (合格で項目が完了になる) と、合格で項目を済みにする章 (completesPathItem) を数える。
 * 数えないのは道場の章、自己申告だけの章、エディターの外のレッスンを含む項目の章 (SQL入門)。板の頭にその旨を出す
 */
export function countsTowardPath(guide: Pick<Guide, "id" | "steps">): boolean {
  const written = Object.values(WRITTEN_PATH_GUIDES).includes(guide.id) || Object.values(WRITTEN_PATH_LESSONS).some((ids) => ids.includes(guide.id));
  return !written || isQuizGuide(guide) || completesPathItem(guide);
}

/**
 * 手で書いた章が複数ある項目の、章の並び (先頭は WRITTEN_PATH_GUIDES の章)。
 * 写した章の並び (LESSON_GUIDE_PATH_ITEMS) と同じ形で、エディターの帯の「次のレッスン」で順に進める
 */
export const WRITTEN_PATH_LESSONS: Readonly<Record<string, readonly string[]>> = {
  // Linux道場の 10 問
  "044f74d0-f940-4053-a778-91a416bca58c": dojoChapters("dojo-lx"),
  // SQL道場の 10 問
  "cbff6e8d-7181-4b6f-971b-e5db0afb919b": dojoChapters("dojo-sql"),
  // Python道場の 10 問
  "9b14e015-d64a-400c-aa4e-d20231fd24db": dojoChapters("dojo-py"),
  // アルゴリズム道場の 10 問
  "6d5865f7-4c57-41f8-b285-c1738edbc507": dojoChapters("dojo-al"),
  // Java道場の 10 問
  "e5f96c1e-ef5e-4c80-8006-015708eaff10": dojoChapters("dojo-java"),
  // SQL入門 (sql-basics) のレッスンのうち、写した章のある 20 本をコースの並び順で (2026-10-14 に本番の lessons を読み取りで確かめた写しの順)。
  // 残りの 3 本 (章末のクイズ) は章が無く、学習パスの画面のコースで進める
  "6ceb49fa-c7c3-4bed-bbf4-334013b9a76a": [
    "sql-basics-what-is-database", "sql-basics-relational-db-basics", "sql-basics-table-row-column", "sql-basics-primary-key-null",
    "select-master-select-basics", "select-master-where-clause", "select-master-comparison-like", "select-master-logical-operators",
    "select-master-order-by", "select-master-limit-offset", "select-master-date-functions",
    "data-aggregation-aggregate-functions", "data-aggregation-handling-null", "data-aggregation-arithmetic-alias",
    "data-aggregation-case-expression", "data-aggregation-distinct", "data-aggregation-string-functions",
    "data-aggregation-group-by", "data-aggregation-having-clause", "data-aggregation-select-execution-order",
  ],
};

/**
 * 手で並べた項目のうち、エディターの外 (学習パスの画面のコース) で進めるレッスンの本数。写した項目の LESSON_GUIDE_PATH_OUTSIDE と同じ形
 */
export const WRITTEN_PATH_OUTSIDE: Readonly<Record<string, number>> = {
  // SQL入門の 23 本のうち、章の無い 3 本 (章末のクイズ)
  "6ceb49fa-c7c3-4bed-bbf4-334013b9a76a": 3,
  // 実践PJ1 ECサイト構築。章 ecsite-ch7-2 は 7-3 の最初の 3 つ (商品一覧) までで、項目の済みの条件は、全手順のあとに EC サイトのリポジトリの URL を講師へ出すこと (外の作業)。章の合格だけでは済みにしない
  "7e06370e-9cdd-50d5-8156-c7df5d4aff87": 1,
};

/**
 * 章の合格では項目が済まない章の、項目の済みの条件 (章 ID から)。板の「学習パスの進みには数えません」の続きに出す。
 * 無い章は、項目に戻って「次の項目へ」で進む案内になる。外の作業で済む項目 (WRITTEN_PATH_OUTSIDE) は、その作業をここに書く
 */
export const UNCOUNTED_DONE_NOTES: Readonly<Record<string, string>> = {
  "ecsite-ch7-2": "項目は、実践プロジェクトの「テスト・提出」の章で、リポジトリの URL を講師に出すと済みになります",
};

const PATH_ITEM_LESSONS: Readonly<Record<string, readonly string[]>> = { ...LESSON_GUIDE_PATH_ITEMS, ...WRITTEN_PATH_LESSONS };
const PATH_ITEM_OUTSIDE: Readonly<Record<string, number>> = { ...LESSON_GUIDE_PATH_OUTSIDE, ...WRITTEN_PATH_OUTSIDE };

/**
 * 学習パスの項目 (team_curriculums.id) から、最初に開く章の ID へ。
 * 項目が複数のレッスンを指すときは、項目が指すレッスンの最初の章 (tools/sandbox/lesson-to-guide が書く LESSON_GUIDE_PATH_ITEMS)。
 * 残りの章へは、エディターの帯の「次のレッスン」で進む (pathLessonStep)。手で書いた章が先
 */
export const PATH_CLOUDEDITOR_GUIDES: Readonly<Record<string, string>> = {
  ...Object.fromEntries(Object.entries(LESSON_GUIDE_PATH_ITEMS).map(([item, ids]) => [item, ids[0]])),
  ...WRITTEN_PATH_GUIDES,
};

export const CLOUDEDITOR_PATH = "/luagate/cloudeditor";

type MemberRole = "owner" | "admin" | "instructor" | "member" | "trial" | null | undefined;

/**
 * 学習パスに「クラウドエディターで開く」を出すか。管理者と講師は常に。
 * sandbox (既定) のときは、学習パスの章を見せると api-go が答えた人 (pathReady。layout の loader の pathEditorReady) にも出す。
 * エディターに入れる (sandboxReady) だけでは出さない。pathReady が分からない (null、undefined) 人は見せない側に倒す
 */
export function canSeePathCloudEditor(
  memberRole: MemberRole,
  pathReady?: boolean | null,
  audience: PathCloudEditorAudience = PATH_CLOUDEDITOR_AUDIENCE,
): boolean {
  if (audience === "everyone") return true;
  const staffRole = memberRole === "owner" || memberRole === "admin" || memberRole === "instructor";
  if (audience === "sandbox") return staffRole || pathReady === true;
  return staffRole;
}

/** 学習パスの項目が指す章 (索引の 1 行。中身は読まない)。表に無い項目と、索引に無い章は null */
export function pathItemGuide(
  curriculumId: string,
  table: Readonly<Record<string, string>> = PATH_CLOUDEDITOR_GUIDES,
): GuideEntry | null {
  return Object.hasOwn(table, curriculumId) ? findGuideEntry(table[curriculumId]) : null;
}

/**
 * 元のレッスン (lessons.id) 対 写した章の ID。学習パス v2 の「1 レッスン 1 項目」の項目は v2 だけの新しい行で、
 * team_curriculums の id (PATH_CLOUDEDITOR_GUIDES の鍵) を持たないので、つないだレッスンから章を引く。
 * 1 つのレッスンに章が複数あれば、索引の並びで先のもの
 */
export function lessonGuideIdIndex(index: Readonly<Record<string, { lessonId?: string }>>): Readonly<Record<string, string>> {
  const out: Record<string, string> = Object.create(null);
  for (const [guideId, entry] of Object.entries(index)) {
    if (entry.lessonId && !(entry.lessonId in out)) out[entry.lessonId] = guideId;
  }
  return out;
}

const GUIDE_ID_BY_LESSON = lessonGuideIdIndex(LESSON_GUIDE_INDEX);

/**
 * 学習パス v2 の項目 (completion が lessons) の手を動かす章の ID。
 * 項目の id で章が引けるときは今までの表が先なので null (呼ぶ側は pathItemGuide を使う)。
 * 引けないときは、つないだレッスンの最初 (lessonIds[0]) の章が、読むだけでなければその ID。
 * 章の合格で api-go が元のレッスンを済みにし、つないだレッスンが全部済めば項目が済みになる
 */
export function pathV2LessonGuideId(
  curriculumId: string,
  lessonIds: readonly string[],
  table: Readonly<Record<string, string>> = PATH_CLOUDEDITOR_GUIDES,
  byLesson: Readonly<Record<string, string>> = GUIDE_ID_BY_LESSON,
): string | null {
  if (pathItemGuide(curriculumId, table)) return null;
  const first = lessonIds[0];
  const guideId = first && first in byLesson ? byLesson[first] : null;
  const entry = findGuideEntry(guideId);
  return entry && pathStripMode(entry) !== "read" ? entry.id : null;
}

/** 項目の頁が使う章。項目の id で引ける章が先、無ければ読み口の材料が持ってきた v2 のレッスンの章 */
export function pathItemGuideOrHint(curriculumId: string, hintGuideId: string | null | undefined): GuideEntry | null {
  return pathItemGuide(curriculumId) ?? findGuideEntry(hintGuideId);
}

/** レッスンの章をつないだ項目のうち、つなぐレッスンが全部済んだら api-go が済ませる項目 (item_guides_gen.go の LessonItems と同じ表) */
const LESSON_ITEMS: Readonly<Record<string, readonly string[]>> = lessonItems();

/**
 * 項目が、クラウドエディターの中の道 (機械の確かめが全部通る、つなぐレッスンの章が全部済む) で済む項目か。
 * そうなら学習パスの画面の「次の学習パスへ」は完了を送らずに進む (送ると、確かめを通さずに済んでしまう)。
 * 読み終えたで済む項目の章 (read) と、章の無い項目、種類がその道を通さない項目 (宿題、確認テスト) は今までどおり送る。
 * 呼ぶ側は、章を見せる相手 (canSeePathCloudEditor) のときだけ使う。受講生にはまだ章を見せないので、今の受講生の完了は変わらない
 */
export function pathItemCompletesInEditor(
  curriculumId: string,
  itemType: string | null | undefined,
  table: Readonly<Record<string, string>> = PATH_CLOUDEDITOR_GUIDES,
  items: Readonly<Record<string, readonly string[]>> = LESSON_ITEMS,
): boolean {
  const entry = pathItemGuide(curriculumId, table);
  if (entry?.itemId === curriculumId) return itemGuideCompletion(entry) !== "read";
  return Object.hasOwn(items, curriculumId) && completionAllowed(itemType, "checks");
}

/** 写した章が表の最初の章になっている項目の、項目の中の章の並び。手で書いた章が先に取った項目と、表に無い項目は null */
function writtenLessons(curriculumId: string, table: Readonly<Record<string, string>>, lessons: Readonly<Record<string, readonly string[]>>): readonly string[] | null {
  if (!Object.hasOwn(table, curriculumId) || !Object.hasOwn(lessons, curriculumId)) return null;
  const list = lessons[curriculumId];
  return list[0] === table[curriculumId] ? list : null;
}

/**
 * 学習パスの項目の中の章の並び (項目が指すレッスンの順)。手で書いた章の項目は、その 1 本だけ。表に無い項目は空。
 * エディターで開けないレッスン (quiz) は入らない
 */
export function pathItemGuideIds(
  curriculumId: string,
  table: Readonly<Record<string, string>> = PATH_CLOUDEDITOR_GUIDES,
  lessons: Readonly<Record<string, readonly string[]>> = PATH_ITEM_LESSONS,
): readonly string[] {
  if (!Object.hasOwn(table, curriculumId)) return [];
  return writtenLessons(curriculumId, table, lessons) ?? [table[curriculumId]];
}

/** 項目が指すレッスンのうち、エディターの外 (学習パスの画面) で解く本数 (quiz)。手で書いた章の項目と表に無い項目は 0 */
export function pathItemOutside(
  curriculumId: string,
  outside: Readonly<Record<string, number>> = PATH_ITEM_OUTSIDE,
  table: Readonly<Record<string, string>> = PATH_CLOUDEDITOR_GUIDES,
  lessons: Readonly<Record<string, readonly string[]>> = PATH_ITEM_LESSONS,
): number {
  return writtenLessons(curriculumId, table, lessons) && Object.hasOwn(outside, curriculumId) ? outside[curriculumId] : 0;
}

/**
 * 項目の中の章の役 (リンク表の種類。事前学習、復習、補助など)。本体の章、手で書いた章の項目、表に無い項目は null。
 * 宿題の項目が前提や復習のリンクで開く章を、宿題そのものと見分けて出すのに使う (tools/sandbox/lesson-to-guide が書く LESSON_GUIDE_PATH_ROLES)
 */
export function pathGuideRole(
  curriculumId: string,
  guideId: string,
  roles: Readonly<Record<string, Readonly<Record<string, string>>>> = LESSON_GUIDE_PATH_ROLES,
  table: Readonly<Record<string, string>> = PATH_CLOUDEDITOR_GUIDES,
  lessons: Readonly<Record<string, readonly string[]>> = PATH_ITEM_LESSONS,
  written: Readonly<Record<string, string>> = WRITTEN_PATH_GUIDES,
): PathLearningAssetRelation | null {
  // 手で書いた章の項目 (道場の 10 問など) は、写した章の役を当てない
  if (Object.hasOwn(written, curriculumId) || !writtenLessons(curriculumId, table, lessons) || !Object.hasOwn(roles, curriculumId)) return null;
  const by = roles[curriculumId];
  const role = Object.hasOwn(by, guideId) ? by[guideId] : null;
  return (PATH_LEARNING_ASSET_RELATIONS as readonly string[]).includes(role ?? "") && role !== "primary" ? role as PathLearningAssetRelation : null;
}

/** 章が項目の本体でないとき、本体の確かめ先まで添える一文。宿題の項目だけ「宿題」と言い、ほかの項目 (task など) には宿題と言わない */
function notTheTask(itemType: string): string {
  return itemType === "homework"
    ? "項目の課題そのものではありません。宿題の中身と出し方は、学習パスの項目の画面で確かめてください"
    : "項目の本文そのものではありません。項目の中身は、学習パスの項目の画面で確かめてください";
}

/** 役の名前 (学習パスの画面の関連コースの札と同じ言葉)。役の無い章は null */
export function pathGuideRoleLabel(role: PathLearningAssetRelation | null): string | null {
  return role ? learningAssetRelationLabel(role) : null;
}

/** エディターの帯に出す、開いている章の役の一言。役の無い章は null */
export function pathGuideRoleNote(role: PathLearningAssetRelation | null, itemType: string): string | null {
  const label = pathGuideRoleLabel(role);
  return label ? `この章は項目の${label}です。${notTheTask(itemType)}` : null;
}

/**
 * 学習パスの入口の帯に出す、項目の中の章の役の一言。役のある章が無い項目は null。
 * 項目の課題は本文にあり、エディターで開くのは前提や復習の章だけ、という項目を、宿題そのものと取り違えさせない
 */
export function pathStripRoleNote(
  curriculumId: string,
  itemType: string,
  roles: Readonly<Record<string, Readonly<Record<string, string>>>> = LESSON_GUIDE_PATH_ROLES,
  table: Readonly<Record<string, string>> = PATH_CLOUDEDITOR_GUIDES,
  lessons: Readonly<Record<string, readonly string[]>> = PATH_ITEM_LESSONS,
  written: Readonly<Record<string, string>> = WRITTEN_PATH_GUIDES,
): string | null {
  const ids = pathItemGuideIds(curriculumId, table, lessons);
  const found = ids.map((id) => pathGuideRole(curriculumId, id, roles, table, lessons, written));
  const labels = [...new Set(found.flatMap((role) => role ? [learningAssetRelationLabel(role)] : []))];
  if (labels.length === 0) return null;
  const named = labels.join("と");
  if (found.every(Boolean)) return `エディターで開くのは、この項目の${named}の章です。${notTheTask(itemType)}`;
  return `エディターで開く ${ids.length} 本の章のうち ${found.filter(Boolean).length} 本は、この項目の${named}の章です`;
}

/** ?from= の鍵と戻り先 */
const BACK_TARGETS = {
  path: { href: "/luagate/path", label: "学習パスに戻る" },
  dojo: { href: "/luagate/dojo", label: "練習道場に戻る" },
} as const;

export type PathFrom = keyof typeof BACK_TARGETS;

/**
 * 学習パス v2 の頁の住所 (?base=)。受けるのは /luagate/path-v2、/luagate/path-v2/<パスの id>、本番の /luagate/paths/<パスの id> だけ (同じサイトの住所で、問いも別の場所も付かない形)。
 * ほかは null で捨てる (外のサイトや別の場所へ飛ばす開いた転送にしない)
 */
export function pathV2Base(raw: string | null | undefined): string | null {
  return typeof raw === "string" && /^\/luagate\/(?:path-v2(?:\/[A-Za-z0-9_-]+)?|paths\/[A-Za-z0-9_-]+)$/.test(raw) && raw !== "/luagate/path-v2/courses" ? raw : null;
}

/** v2 の項目の頁の住所 (<base>/<番号>)。番号が無い、または正の整数でなければ base (v2 の一覧) */
export function pathV2ItemHref(base: string, num: string | number | null | undefined): string {
  const text = String(num ?? "");
  return /^\d{1,6}$/.test(text) && Number(text) >= 1 ? `${base}/${Number(text)}` : base;
}

/**
 * ?from= から戻り先を引く。表に無い鍵は null (戻るリンクを出さない)。
 * 学習パス v2 から来たとき (v2 の base と num を渡す) は、v1 の一覧でなく v2 の項目の頁へ戻す
 */
export function pathBackLink(from: string | null | undefined, v2?: { base: string | null; num?: string | null } | null): { href: string; label: string } | null {
  if (typeof from !== "string" || !Object.hasOwn(BACK_TARGETS, from)) return null;
  const target = BACK_TARGETS[from as PathFrom];
  const base = from === "path" ? pathV2Base(v2?.base) : null;
  return base ? { href: pathV2ItemHref(base, v2?.num), label: target.label } : target;
}

/*
学習パスの項目から関連教材のレッスン (/learn/courses/…) を開くときの印。レッスンの頁の帯に、元の項目へ戻る入口を出す。
  ?from=path&num=<項目の番号>&item=<札の番号 1-40>&base=<項目の頁の置き場>
戻り先は表の形に合う住所だけを組む (外のサイトや別の場所へ飛ばす開いた転送にしない)
*/
const ITEM_BASE = /^\/luagate\/path(?:-v2)?(?:\/[A-Za-z0-9_-]+)?$/;

function itemBase(raw: string | null | undefined): string {
  // 本番の v2 の項目の頁 (/luagate/paths/<パスの id>) も受ける。前は受けず、v2 の人の「項目 n-n に戻る」が v1 の /luagate/path/<番号> へ行き、別の項目に着いた
  if (typeof raw === "string" && ITEM_BASE.test(raw) && raw !== "/luagate/path-v2/courses") return raw;
  return pathV2Base(raw) ?? "/luagate/path";
}

/** 項目の頁から開く関連教材の住所に足す問い。番号が正の整数でなければ空 (印を付けない) */
export function pathItemReturnQuery(item: { num: string | number; label?: string | number | null; base?: string | null }): string {
  const num = String(item.num);
  if (!/^\d{1,6}$/.test(num) || Number(num) < 1) return "";
  const params = new URLSearchParams({ from: "path", num: String(Number(num)) });
  const label = item.label == null ? "" : String(item.label);
  if (/^\d{1,3}(?:-\d{1,3}){0,2}$/.test(label)) params.set("item", label);
  const base = itemBase(item.base);
  if (base !== "/luagate/path") params.set("base", base);
  return params.toString();
}

/** レッスンの頁の ?from=path から、元の項目へ戻る入口を引く。印が無い・番号が正しくなければ null */
export function pathItemReturnLink(searchParams: URLSearchParams): { href: string; label: string } | null {
  const num = searchParams.get("num");
  if (searchParams.get("from") !== "path" || !num || !/^\d{1,6}$/.test(num) || Number(num) < 1) return null;
  const item = searchParams.get("item");
  const label = item && /^\d{1,3}(?:-\d{1,3}){0,2}$/.test(item) ? `項目 ${item} に戻る` : "学習パスの項目に戻る";
  return { href: `${itemBase(searchParams.get("base"))}/${Number(num)}`, label };
}

/** 章を開く URL。学習パスから開くときは from=path、練習道場から開くときは from=dojo を付ける */
export function cloudEditorGuideHref(guideId: string, from: PathFrom = "path"): string {
  return `${CLOUDEDITOR_PATH}?${new URLSearchParams({ guide: guideId, from }).toString()}`;
}

/**
 * 学習パスの項目に出す席の一言。
 * - unavailable はクラウドエディターに入れない人 (api-go の判定)。帯ごと出さず、何も言わない
 * - open と full は、その章の種類の空き。読めなかったときは unknown で何も言わない
 */
export type PathSeat =
  | { state: "unavailable" }
  | { state: "unknown" }
  | { state: "open"; label: string; free: number }
  | { state: "full"; label: string };

export function pathSeatOf(guide: { kind: string }, ready: boolean, capacity: KindCapacity[] | null): PathSeat {
  if (!ready) return { state: "unavailable" };
  const item = capacity?.find((entry) => entry.name === guide.kind);
  if (!item) return { state: "unknown" };
  const label = kindOf(guide.kind)?.label ?? guide.kind;
  return item.free > 0 ? { state: "open", label, free: item.free } : { state: "full", label };
}

/** 席の一言の文。unknown と unavailable は何も言わない */
export function pathSeatText(seat: PathSeat): string | null {
  if (seat.state === "open") return `${seat.label} は、いま空いています (あと ${seat.free} 人まで)`;
  if (seat.state === "full") return `${seat.label} は、いま満員です。少し待つか、先に次の項目へ進めます`;
  return null;
}

/**
 * 項目の章の使い方。
 * - read は読むだけの章 (readOnly) と、外での作業の自己申告だけの章 (attestOnly)。帯を出さず、エディターへも送らない。
 *   項目はいつもどおりブラウザで進める (外での作業の章は、項目のページに手順と記録の欄が出る。LgAttestSteps)
 * - hands-on はそれ以外。見せる相手が PC で開くと、最初からエディターの章が開く。
 *   本文を読んでから端末で試す章 (envFrom) も、分けずに同じ形にする (2026-10-16 代表「わざわざ分けるのは体験がよくない」)
 */
export type PathStripMode = "read" | "hands-on";

export function pathStripMode(guide: { readOnly?: boolean; attestOnly?: boolean }): PathStripMode {
  return guide.readOnly || guide.attestOnly ? "read" : "hands-on";
}

/**
 * 外での作業の章の項目の表示順 (学習パスの項目の番号の URL)。学習パスの並びと番号の URL は変えない決まり (WRITTEN_PATH_GUIDES の頭)。
 * 学習パスから来ていない (?num= が無い) ときの送り先に使う
 */
const ATTEST_ITEM_NUMBERS: Readonly<Record<string, number>> = {
  "mattermost-join": 2,
  "hw-folder-tidy": 27,
  "hw-pc-spec": 28,
};

/**
 * 消した提出の章 (宿題 27、28。2026-10-17 に外での作業の章 1 本へ戻した) の古い id。
 * 保存された古い URL (?guide=hw-folder-tidy-submit など) を開いた人を、その項目のページへ送る
 */
const RETIRED_GUIDE_ITEM_NUMBERS: Readonly<Record<string, number>> = {
  "hw-folder-tidy-submit": 27,
  "hw-pc-spec-submit": 28,
};

/**
 * 消した提出の章 (2026-10-20 の代表の決定で、講師への提出を外した) の古い id から、同じフォルダを開く元の章へ。
 * 保存された古い URL (?guide=hw-permissions-submit など) を開いた人を、元の章へ送る
 */
const RETIRED_SUBMIT_GUIDES: Readonly<Record<string, string>> = {
  "hw-web-flow-submit": "hw-web-flow",
  "hw-login-flow-submit": "hw-login-flow",
  "hw-permissions-submit": "hw-permissions",
  "db-setup-postgres-submit": "db-setup-postgres",
  "hw-oop-report-submit": "hw-oop-report",
  "hw-class-diagram-submit": "hw-class-diagram",
  "hw-sequence-diagram-submit": "hw-sequence-diagram",
  "hw-web-server-submit": "hw-web-server",
};

/** 消した章の古い URL の送り先。消した章でなければ null。元の章へ送るときは、ほかの問い (from、num など) をそのまま残す */
export function retiredGuideItemHref(guideId: string | null | undefined, searchParams?: URLSearchParams): string | null {
  if (!guideId) return null;
  if (Object.hasOwn(RETIRED_GUIDE_ITEM_NUMBERS, guideId)) return `/luagate/path/${RETIRED_GUIDE_ITEM_NUMBERS[guideId]}`;
  if (!Object.hasOwn(RETIRED_SUBMIT_GUIDES, guideId)) return null;
  const params = new URLSearchParams(searchParams);
  params.set("guide", RETIRED_SUBMIT_GUIDES[guideId]);
  return `${CLOUDEDITOR_PATH}?${params.toString()}`;
}

/**
 * 外での作業の章をエディターの URL で開いたときの送り先 (学習パスの項目のページ)。そういう章でなければ null。
 * 学習パスから来たとき (?from=path&num=) はその番号、そうでなければ表の番号
 */
export function attestItemPageHref(entry: { id: string; attestOnly?: boolean } | null | undefined, searchParams: URLSearchParams): string | null {
  if (!entry?.attestOnly) return null;
  const num = searchParams.get("num");
  if (searchParams.get("from") === "path" && num && /^\d{1,6}$/.test(num) && Number(num) >= 1) return `/luagate/path/${Number(num)}`;
  return Object.hasOwn(ATTEST_ITEM_NUMBERS, entry.id) ? `/luagate/path/${ATTEST_ITEM_NUMBERS[entry.id]}` : "/luagate/path";
}

/** 帯の見出し。帯はエディターへ送れないとき (スマホの幅、満員) にだけ出る */
export const PATH_STRIP_HEADING = "クラウドエディターで、書いて動かしながら学べます";

/**
 * 学習パスの項目の画面を、エディターへ送らずに開く問い (?editor=off)。
 * エディターの中から項目の画面へ行くリンク (確認問題を解く、元の画面を見る) に付け、送り返しで同じ章へ戻る輪にしない
 */
export const PATH_ITEM_STAY_QUERY = { key: "editor", value: "off" } as const;

export function pathItemStayHref(path: string): string {
  return `${path}?${PATH_ITEM_STAY_QUERY.key}=${PATH_ITEM_STAY_QUERY.value}`;
}

export function wantsPathItemPage(searchParams: URLSearchParams): boolean {
  return searchParams.get(PATH_ITEM_STAY_QUERY.key) === PATH_ITEM_STAY_QUERY.value;
}
